import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { Search, X, Plus, Check, Lock, ChevronDown, ChevronRight, ChevronLeft } from 'lucide-react';
import { supabase } from '../../supabaseClient';
import {
  CATALOG_CHIPS,
  PLANT_GROUP_ORDER,
  FLOWER_GROUP_ORDER,
  PLANT_GROUP_LABELS,
  GROWTH_HABIT_LABELS,
} from '../../utils/constants';

const PAGE_SIZE = 30;

const SELECT_FIELDS = [
  'id', 'plant', 'variety', 'category', 'type', 'season', 'harvest_season',
  'days_to_production', 'preferred_start_method', 'weeks_indoors_before_transplant',
  'spacing', 'frost_tolerant', 'direct_sow_weeks_before_last_frost',
  'growth_habit', 'plant_group', 'short_season', 'disease_resistant',
  'heat_tolerant', 'bolt_resistant', 'space_saving', 'sun_requirement',
  'drought_tolerant', 'container_friendly', 'water_needs',
].join(', ');

// Plants that display growth_habit label inline in the variety row
const SHOW_GROWTH_HABIT_FOR = new Set([
  'Tomato', 'Cucumber', 'Bean', 'Pea', 'Squash', 'Summer Squash',
  'Winter Squash', 'Pumpkin', 'Melon', 'Watermelon',
]);

// Per-plant filter chips shown within each expanded variety list
const PLANT_SPECIFIC_CHIPS = {
  Tomato: [
    { id: 'determinate', label: 'Determinate', match: r => r.growth_habit === 'determinate' },
    { id: 'indeterminate', label: 'Indeterminate', match: r => r.growth_habit === 'indeterminate' },
    { id: 'short_season', label: 'Short Season', match: r => r.short_season },
    { id: 'disease_resistant', label: 'Disease Resistant', match: r => r.disease_resistant },
    { id: 'space_saving', label: 'Space Saving', match: r => r.space_saving },
  ],
  Cucumber: [
    { id: 'bush', label: 'Bush', match: r => r.growth_habit === 'bush' },
    { id: 'vining', label: 'Vining', match: r => r.growth_habit === 'vining' },
    { id: 'short_season', label: 'Short Season', match: r => r.short_season },
  ],
  Bean: [
    { id: 'bush', label: 'Bush', match: r => r.growth_habit === 'bush' },
    { id: 'climbing', label: 'Climbing/Pole', match: r => r.growth_habit === 'climbing' },
    { id: 'short_season', label: 'Short Season', match: r => r.short_season },
  ],
  Pepper: [
    { id: 'short_season', label: 'Short Season', match: r => r.short_season },
    { id: 'heat_tolerant', label: 'Heat Tolerant', match: r => r.heat_tolerant },
  ],
  Lettuce: [
    { id: 'bolt_resistant', label: 'Bolt Resistant', match: r => r.bolt_resistant },
    { id: 'heat_tolerant', label: 'Heat Tolerant', match: r => r.heat_tolerant },
  ],
};

const GENERAL_CHIPS = [
  { id: 'short_season', label: 'Short Season', match: r => r.short_season },
  { id: 'space_saving', label: 'Space Saving', match: r => r.space_saving },
  { id: 'full_sun', label: 'Full Sun', match: r => r.sun_requirement === 'full_sun' },
  { id: 'part_sun', label: 'Part Sun', match: r => r.sun_requirement === 'part_sun' },
];

const DROUGHT_CHIP = {
  id: 'drought_tolerant', label: 'Drought Tolerant', match: r => r.drought_tolerant,
};

const getVarietyFilterChips = (plantName, category) => {
  if (PLANT_SPECIFIC_CHIPS[plantName]) return PLANT_SPECIFIC_CHIPS[plantName];
  const chips = [...GENERAL_CHIPS];
  if (category === 'flower' || category === 'herb') chips.push(DROUGHT_CHIP);
  return chips;
};

// Group vegetable records by primary plant_group into a nested map
const buildVegetableGroups = (records) => {
  const knownGroups = new Set(PLANT_GROUP_ORDER);
  const groups = {};
  records.forEach(record => {
    const firstGroup = record.plant_group && record.plant_group[0];
    const groupKey = (firstGroup && knownGroups.has(firstGroup)) ? firstGroup : 'specialty';
    if (!groups[groupKey]) groups[groupKey] = {};
    if (!groups[groupKey][record.plant]) groups[groupKey][record.plant] = [];
    groups[groupKey][record.plant].push(record);
  });
  return groups;
};

// Group flower records by plant_group (cut_flower, pollinator, edible)
const buildFlowerGroups = (records) => {
  const knownGroups = new Set(FLOWER_GROUP_ORDER);
  const fallback = FLOWER_GROUP_ORDER[FLOWER_GROUP_ORDER.length - 1];
  const groups = {};
  records.forEach(record => {
    const firstGroup = record.plant_group && record.plant_group[0];
    const groupKey = (firstGroup && knownGroups.has(firstGroup)) ? firstGroup : fallback;
    if (!groups[groupKey]) groups[groupKey] = {};
    if (!groups[groupKey][record.plant]) groups[groupKey][record.plant] = [];
    groups[groupKey][record.plant].push(record);
  });
  return groups;
};

// Group non-vegetable records by plant name
const buildPlantMap = (records) => {
  const map = {};
  records.forEach(record => {
    if (!map[record.plant]) map[record.plant] = [];
    map[record.plant].push(record);
  });
  return map;
};

// -- VarietyRow ----------------------------------------------------------------

const VarietyRow = React.memo(({ record, isAdded, isAtLimit, onAdd, onUpgrade }) => {
  const showGrowthHabit = SHOW_GROWTH_HABIT_FOR.has(record.plant) && record.growth_habit;
  const habitLabel = showGrowthHabit
    ? (GROWTH_HABIT_LABELS[record.growth_habit] || record.growth_habit)
    : null;

  const seasonColor =
    record.season === 'warm' ? 'bg-orange-100 text-orange-700' : 'bg-blue-100 text-blue-700';
  const seasonLabel =
    record.season === 'warm' ? 'Warm' : record.season === 'cool' ? 'Cool' : null;

  const handleClick = () => {
    if (isAdded) return;
    if (isAtLimit) { onUpgrade?.(); return; }
    onAdd(record);
  };

  return (
    <tr
      className="border-b border-gray-100 last:border-0 hover:bg-gray-50 transition-colors"
      style={{ minHeight: 44 }}
    >
      <td className="px-4 py-3">
        <span className="font-semibold text-[#2F5233] text-sm">{record.plant}</span>
        {record.variety && (
          <>
            <br />
            <span className="text-[#52A675] text-xs">{record.variety}</span>
          </>
        )}
      </td>

      <td className="px-2 py-3 whitespace-nowrap">
        {seasonLabel && (
          <span className={`inline-block px-2 py-0.5 rounded-full text-xs font-medium ${seasonColor}`}>
            {seasonLabel}
          </span>
        )}
      </td>

      <td className="px-2 py-3 text-xs text-gray-500 hidden md:table-cell whitespace-nowrap">
        <span className="flex items-center gap-1.5">
          {habitLabel && <span className="text-gray-500">{habitLabel}</span>}
          {habitLabel && record.days_to_production && <span className="text-gray-300">·</span>}
          {record.days_to_production
            ? `${record.days_to_production}d`
            : (!habitLabel ? '—' : '')}
        </span>
      </td>

      <td className="px-3 py-3 text-right whitespace-nowrap" style={{ minWidth: 56 }}>
        {isAdded ? (
          <span
            className="inline-flex items-center gap-1 px-2 py-1 text-xs text-[#52A675] font-medium"
            title="Already in My Plants"
          >
            <Check size={14} />
            <span className="hidden sm:inline">In My Plants</span>
          </span>
        ) : (
          <button
            onClick={handleClick}
            title={isAtLimit ? 'Free limit reached — upgrade to Pro' : `Add ${record.plant} to My Plants`}
            aria-label={isAtLimit ? 'Upgrade to Pro to add more plants' : `Add ${record.plant}${record.variety ? ` (${record.variety})` : ''} to My Plants`}
            className={`inline-flex items-center justify-center w-8 h-8 rounded-lg transition-colors ${isAtLimit
              ? 'bg-amber-100 text-amber-600 hover:bg-amber-200'
              : 'bg-[#52A675] hover:bg-[#4A9168] text-white'
              }`}
          >
            {isAtLimit ? <Lock size={14} /> : <Plus size={16} />}
          </button>
        )}
      </td>
    </tr>
  );
});

// -- VarietyList ---------------------------------------------------------------
// Filter chips + paginated variety table for an expanded plant

const VarietyList = ({ plantName, category, varieties, isAdded, isAtLimit, onAdd, onUpgrade }) => {
  const [activeFilter, setActiveFilter] = useState(null);
  const [page, setPage] = useState(0);
  const topRef = useRef(null);

  // Scroll the top of this variety list into view whenever the page changes
  useEffect(() => {
    if (topRef.current) {
      topRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }, [page]);

  const allChips = getVarietyFilterChips(plantName, category);
  // Only show chips where the filter would actually split the list
  // (some varieties match AND some don't — chips where all match are useless)
  const availableChips = allChips.filter(chip =>
    varieties.some(chip.match) && varieties.some(v => !chip.match(v))
  );

  const activeChipDef = allChips.find(c => c.id === activeFilter);
  const filtered = activeFilter && activeChipDef
    ? varieties.filter(activeChipDef.match)
    : varieties;

  const totalPages = Math.ceil(filtered.length / PAGE_SIZE);
  const paged = filtered.slice(page * PAGE_SIZE, (page + 1) * PAGE_SIZE);

  const handleFilterClick = (chipId) => {
    setActiveFilter(prev => (prev === chipId ? null : chipId));
    setPage(0);
  };

  return (
    <div ref={topRef} className="bg-gray-50 border-t border-gray-100">
      {availableChips.length > 0 && (
        <div className="flex flex-wrap gap-1.5 px-4 pt-3 pb-2">
          {availableChips.map(chip => (
            <button
              key={chip.id}
              onClick={() => handleFilterClick(chip.id)}
              className={`px-2.5 py-1 rounded-full text-xs font-medium transition-colors ${activeFilter === chip.id
                ? 'bg-[#52A675] text-white'
                : 'bg-white border border-gray-200 text-gray-600 hover:border-[#52A675] hover:text-[#52A675]'
                }`}
            >
              {chip.label}
            </button>
          ))}
        </div>
      )}

      <table className="w-full">
        <thead className="sr-only">
          <tr>
            <th>Plant / Variety</th><th>Season</th><th>Details</th><th>Add</th>
          </tr>
        </thead>
        <tbody>
          {paged.length === 0 ? (
            <tr>
              <td colSpan={4} className="py-6 text-center text-gray-400 text-sm px-4">
                No varieties match this filter.
              </td>
            </tr>
          ) : (
            paged.map(record => (
              <VarietyRow
                key={record.id}
                record={record}
                isAdded={isAdded(record)}
                isAtLimit={isAtLimit && !isAdded(record)}
                onAdd={onAdd}
                onUpgrade={onUpgrade}
              />
            ))
          )}
        </tbody>
      </table>

      {totalPages > 1 && (
        <div className="flex items-center justify-between px-4 py-2 border-t border-gray-100">
          <button
            onClick={() => setPage(p => p - 1)}
            disabled={page === 0}
            className="flex items-center gap-1 px-2.5 py-1 rounded-lg bg-white border border-gray-200 hover:border-[#52A675] disabled:opacity-40 disabled:cursor-not-allowed transition-colors text-xs text-gray-600"
          >
            <ChevronLeft size={13} /> Prev
          </button>
          <span className="text-xs text-gray-400">
            {page + 1} / {totalPages} &middot; {filtered.length} varieties
          </span>
          <button
            onClick={() => setPage(p => p + 1)}
            disabled={page >= totalPages - 1}
            className="flex items-center gap-1 px-2.5 py-1 rounded-lg bg-white border border-gray-200 hover:border-[#52A675] disabled:opacity-40 disabled:cursor-not-allowed transition-colors text-xs text-gray-600"
          >
            Next <ChevronRight size={13} />
          </button>
        </div>
      )}
    </div>
  );
};

// -- PlantRow ------------------------------------------------------------------
// A single plant within a group or category, expandable to its variety list

const PlantRow = ({ plantName, varieties, isExpanded, onToggle, isAdded, isAtLimit, onAdd, onUpgrade, indent }) => {
  const category = varieties[0]?.category;
  return (
    <div className="border-b border-gray-100 last:border-0">
      <button
        onClick={onToggle}
        className={`w-full flex items-center justify-between py-2.5 hover:bg-gray-50 transition-colors text-left ${indent ? 'pl-12 pr-4' : 'px-4'}`}
      >
        <span className={`text-sm ${indent ? 'font-medium text-gray-600' : 'font-semibold text-[#2F5233]'}`}>{plantName}</span>
        <span className="flex items-center gap-2 text-xs text-gray-400 flex-shrink-0">
          <span>{varieties.length} {varieties.length === 1 ? 'variety' : 'varieties'}</span>
          {isExpanded ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
        </span>
      </button>

      {isExpanded && (
        <VarietyList
          plantName={plantName}
          category={category}
          varieties={varieties}
          isAdded={isAdded}
          isAtLimit={isAtLimit}
          onAdd={onAdd}
          onUpgrade={onUpgrade}
        />
      )}
    </div>
  );
};

// -- PlantGroupSection ---------------------------------------------------------
// Accordion group for Vegetables (group -> plants -> varieties)

const PlantGroupSection = ({
  groupKey, plantMap, isExpanded, onToggleGroup,
  expandedPlant, onTogglePlant, isAdded, isAtLimit, onAdd, onUpgrade,
}) => {
  const label = PLANT_GROUP_LABELS[groupKey] || groupKey;
  const totalVarieties = Object.values(plantMap).reduce((sum, arr) => sum + arr.length, 0);
  const plantNames = Object.keys(plantMap).sort();

  return (
    <div className="border-b border-gray-100 last:border-0">
      <button
        onClick={onToggleGroup}
        className="w-full flex items-center justify-between px-4 py-3.5 bg-[#f0f9f2] hover:bg-[#e8f5e9] transition-colors text-left"
      >
        <span className="font-bold text-[#2F5233] text-sm">{label}</span>
        <span className="flex items-center gap-2 text-xs text-gray-500 flex-shrink-0">
          <span>{totalVarieties} {totalVarieties === 1 ? 'variety' : 'varieties'}</span>
          {isExpanded ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
        </span>
      </button>

      {isExpanded && (
        <div className="border-t border-gray-100">
          {plantNames.map(plantName => (
            <PlantRow
              key={plantName}
              plantName={plantName}
              varieties={plantMap[plantName]}
              isExpanded={expandedPlant === plantName}
              onToggle={() => onTogglePlant(groupKey, plantName)}
              isAdded={isAdded}
              isAtLimit={isAtLimit}
              onAdd={onAdd}
              onUpgrade={onUpgrade}
              indent
            />
          ))}
        </div>
      )}
    </div>
  );
};

// -- Shared table header -------------------------------------------------------

const CatalogTableHead = () => (
  <thead className="bg-gray-50 border-b-2 border-gray-100">
    <tr>
      <th className="text-left px-4 py-3 text-sm font-semibold text-[#2F5233]">Plant</th>
      <th className="text-left px-2 py-3 text-sm font-semibold text-[#2F5233]">Season</th>
      <th className="text-left px-2 py-3 text-sm font-semibold text-[#2F5233] hidden md:table-cell">Details</th>
      <th className="text-right px-3 py-3 text-sm font-semibold text-[#2F5233]">Add</th>
    </tr>
  </thead>
);

// -- CatalogBrowser (main) -----------------------------------------------------

const CatalogBrowser = ({
  seeds, isPro, seedLimit, onAdd, onUpgrade, onSwitchToAdd, simplified = false,
  // Optional controlled props — when provided the internal filter bar is hidden
  activeChip: activeChipProp,
  onChipChange: onChipChangeProp,
  searchTerm: searchTermProp,
  onSearchChange: onSearchChangeProp,
}) => {
  const isControlled = activeChipProp !== undefined;
  const [activeChipInternal, setActiveChipInternal] = useState('vegetables');
  const activeChip = isControlled ? activeChipProp : activeChipInternal;

  // Search
  const [searchTermInternal, setSearchTermInternal] = useState('');
  const searchTerm = isControlled ? (searchTermProp ?? '') : searchTermInternal;
  const setSearchTerm = isControlled ? onSearchChangeProp : setSearchTermInternal;
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searchTotal, setSearchTotal] = useState(0);
  const [searchPage, setSearchPage] = useState(0);

  // "All" chip pagination (not used in simplified mode)
  const [allPage, setAllPage] = useState(0);
  const [allTotal, setAllTotal] = useState(0);
  const [allRecords, setAllRecords] = useState([]);

  // Simplified mode pagination (used in PlantPickerModal)
  const [simplifiedPage, setSimplifiedPage] = useState(0);
  const simplifiedTopRef = useRef(null);

  // Scroll to top of simplified list whenever page changes
  useEffect(() => {
    if (simplified && simplifiedTopRef.current) {
      simplifiedTopRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }, [simplifiedPage, simplified]);

  const [loading, setLoading] = useState(false);

  // Category data cache stored in state (triggers memos on update)
  const [categoryCache, setCategoryCache] = useState({});

  // Accordion state -- Vegetables
  const [expandedGroups, setExpandedGroups] = useState(new Set());
  // { [groupKey]: plantName | null } -- one plant expanded per group
  const [expandedVegPlant, setExpandedVegPlant] = useState({});

  // Accordion state -- Flowers (grouped by plant_group)
  const [expandedFlowerGroups, setExpandedFlowerGroups] = useState(new Set());
  const [expandedFlowerPlant, setExpandedFlowerPlant] = useState({});

  // Accordion state -- Herbs / Fruit (single plant at a time)
  const [expandedPlant, setExpandedPlant] = useState(null);

  // Auto-focus search in simplified mode
  const searchInputRef = React.useRef(null);
  useEffect(() => {
    if (simplified && searchInputRef.current) {
      searchInputRef.current.focus();
    }
  }, [simplified]);

  // O(1) added lookup using catalog_id
  const addedCatalogIds = useMemo(
    () => new Set(seeds.filter(s => s.catalog_id).map(s => s.catalog_id)),
    [seeds]
  );
  const isAdded = useCallback(
    (record) => addedCatalogIds.has(record.id),
    [addedCatalogIds]
  );

  const isAtLimit = !isPro && seeds.length >= seedLimit;

  // Debounce search
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchTerm);
      setSearchPage(0);
    }, 250);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  // -- Data loading ------------------------------------------------------------
  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      setLoading(true);

      // Search mode -- always server-side
      if (debouncedSearch) {
        const trimmed = debouncedSearch.trim();
        let query = supabase
          .from('catalog_plants')
          .select(SELECT_FIELDS, { count: 'exact' });

        if (activeChip === 'vegetables') query = query.eq('category', 'vegetable');
        else if (activeChip === 'herbs') query = query.eq('category', 'herb');
        else if (activeChip === 'flowers') query = query.eq('category', 'flower');
        else if (activeChip === 'fruit') query = query.eq('category', 'fruit'); else if (activeChip === 'fungi') query = query.eq('category', 'fungi');
        query = query.or(`plant.ilike.%${trimmed}%,variety.ilike.%${trimmed}%`);

        const from = searchPage * PAGE_SIZE;
        const { data, count, error } = await query
          .order('plant', { ascending: true })
          .order('variety', { ascending: true })
          .range(from, from + PAGE_SIZE - 1);

        if (!cancelled) {
          setSearchResults(error ? [] : (data || []));
          setSearchTotal(error ? 0 : (count || 0));
          setLoading(false);
        }
        return;
      }

      // All chip -- paginated server-side
      if (activeChip === 'all') {
        const from = allPage * PAGE_SIZE;
        const { data, count, error } = await supabase
          .from('catalog_plants')
          .select(SELECT_FIELDS, { count: 'exact' })
          .order('plant', { ascending: true })
          .order('variety', { ascending: true })
          .range(from, from + PAGE_SIZE - 1);

        if (!cancelled) {
          setAllRecords(error ? [] : (data || []));
          setAllTotal(error ? 0 : (count || 0));
          setLoading(false);
        }
        return;
      }

      // Category chips (vegetables, herbs, flowers, fruit)
      if (categoryCache[activeChip]) {
        if (!cancelled) setLoading(false);
        return;
      }

      const categoryMap = {
        vegetables: 'vegetable',
        herbs: 'herb',
        flowers: 'flower',
        fruit: 'fruit',
        fungi: 'fungi',
      };
      const categoryValue = categoryMap[activeChip];
      if (!categoryValue) {
        if (!cancelled) setLoading(false);
        return;
      }

      const { data, error } = await supabase
        .from('catalog_plants')
        .select(SELECT_FIELDS)
        .eq('category', categoryValue)
        .order('plant', { ascending: true })
        .order('variety', { ascending: true });

      if (!cancelled) {
        setCategoryCache(prev => ({ ...prev, [activeChip]: error ? [] : (data || []) }));
        setLoading(false);
      }
    };

    load();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeChip, debouncedSearch, searchPage, allPage]);

  // Derived accordion data
  const vegetableGroups = useMemo(() => {
    const data = categoryCache.vegetables;
    if (!data) return null;
    return buildVegetableGroups(data);
  }, [categoryCache.vegetables]);

  const flowerGroups = useMemo(() => {
    const data = categoryCache.flowers;
    if (!data) return null;
    return buildFlowerGroups(data);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [categoryCache.flowers]);

  const singleCategoryPlantMap = useMemo(() => {
    if (!['herbs', 'fruit', 'fungi'].includes(activeChip)) return null;
    const data = categoryCache[activeChip];
    if (!data) return null;
    return buildPlantMap(data);
  }, [activeChip, categoryCache]);

  // -- Handlers ----------------------------------------------------------------
  const handleChipChange = (chipId) => {
    if (isControlled) onChipChangeProp(chipId);
    else setActiveChipInternal(chipId);
    setAllPage(0);
    setSearchPage(0);
    setSimplifiedPage(0);
  };

  const handleClearSearch = () => {
    setSearchTerm('');
    setDebouncedSearch('');
    setSearchPage(0);
    setSimplifiedPage(0);
  };

  const toggleGroup = (groupKey) => {
    setExpandedGroups(prev => {
      const next = new Set(prev);
      if (next.has(groupKey)) next.delete(groupKey);
      else next.add(groupKey);
      return next;
    });
  };

  const toggleVegPlant = (groupKey, plantName) => {
    setExpandedVegPlant(prev => ({
      ...prev,
      [groupKey]: prev[groupKey] === plantName ? null : plantName,
    }));
  };

  const toggleFlowerGroup = (groupKey) => {
    setExpandedFlowerGroups(prev => {
      const next = new Set(prev);
      if (next.has(groupKey)) next.delete(groupKey);
      else next.add(groupKey);
      return next;
    });
  };

  const toggleFlowerPlant = (groupKey, plantName) => {
    setExpandedFlowerPlant(prev => ({
      ...prev,
      [groupKey]: prev[groupKey] === plantName ? null : plantName,
    }));
  };

  const toggleSinglePlant = (plantName) => {
    setExpandedPlant(prev => (prev === plantName ? null : plantName));
  };

  // -- Render ------------------------------------------------------------------
  const isSearching = !!debouncedSearch;
  const chipLabel = CATALOG_CHIPS.find(c => c.id === activeChip)?.label || '';
  const searchTotalPages = Math.ceil(searchTotal / PAGE_SIZE);
  const allTotalPages = Math.ceil(allTotal / PAGE_SIZE);

  return (
    <div>
      {/* Category chips + search — hidden when parent controls them (stickyContent) */}
      {!isControlled && (
        <div className="mb-4">
          {/* Category chips -- hide 'all' chip in simplified mode */}
          <div className="flex flex-wrap gap-2 mb-3">
            {CATALOG_CHIPS.filter(chip => !simplified || chip.id !== 'all').map(chip => (
              <button
                key={chip.id}
                onClick={() => handleChipChange(chip.id)}
                className={`px-3 py-1.5 rounded-full text-sm font-medium transition-colors whitespace-nowrap ${activeChip === chip.id
                  ? 'bg-[#52A675] text-white'
                  : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                  }`}
              >
                {chip.label}
              </button>
            ))}
          </div>

          {/* Search */}
          <div className="relative mb-0">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={16} />
            <input
              ref={searchInputRef}
              type="text"
              placeholder={`Search${activeChip !== 'all' ? ` in ${chipLabel}` : ' all plants'}\u2026`}
              value={searchTerm}
              onChange={e => setSearchTerm(e.target.value)}
              className="w-full pl-9 pr-8 py-2.5 border-2 border-gray-200 rounded-xl focus:border-[#52A675] focus:ring-0 focus:outline-none transition-colors text-[#2F5233] bg-white text-sm"
            />
            {searchTerm && (
              <button
                onClick={handleClearSearch}
                className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors"
                aria-label="Clear search"
              >
                <X size={15} />
              </button>
            )}
          </div>
        </div>
      )}{/* end filter bar */}

      {/* Main content card */}
      <div className="bg-white border-2 border-gray-100 rounded-2xl overflow-hidden mb-4">
        {loading ? (
          <div className="py-12 text-center text-gray-400 text-sm">Loading…</div>

        ) : isSearching ? (
          <>
            <table className="w-full">
              <CatalogTableHead />
              <tbody>
                {searchResults.length === 0 ? (
                  <tr>
                    <td colSpan={4} className="py-12 text-center px-4">
                      <p className="text-gray-500 text-sm mb-2">
                        &ldquo;{debouncedSearch}&rdquo; wasn&apos;t found in {chipLabel}.
                      </p>
                      {activeChip !== 'all' && (
                        <button
                          onClick={() => handleChipChange('all')}
                          className="text-[#52A675] hover:text-[#4A9168] text-sm font-medium"
                        >
                          Search all plants &rarr;
                        </button>
                      )}
                    </td>
                  </tr>
                ) : (
                  searchResults.map(record => (
                    <VarietyRow
                      key={record.id}
                      record={record}
                      isAdded={isAdded(record)}
                      isAtLimit={isAtLimit && !isAdded(record)}
                      onAdd={onAdd}
                      onUpgrade={onUpgrade}
                    />
                  ))
                )}
              </tbody>
            </table>
            {!simplified && searchTotalPages > 1 && (
              <div className="flex items-center justify-between px-4 py-3 border-t border-gray-100">
                <button
                  onClick={() => setSearchPage(p => p - 1)}
                  disabled={searchPage === 0}
                  className="flex items-center gap-1 px-3 py-1.5 rounded-lg bg-gray-100 hover:bg-gray-200 disabled:opacity-40 disabled:cursor-not-allowed transition-colors text-sm text-gray-600"
                >
                  <ChevronLeft size={15} /> Previous
                </button>
                <span className="text-sm text-gray-400">
                  Page {searchPage + 1} of {searchTotalPages} &middot; {searchTotal} results
                </span>
                <button
                  onClick={() => setSearchPage(p => p + 1)}
                  disabled={searchPage >= searchTotalPages - 1}
                  className="flex items-center gap-1 px-3 py-1.5 rounded-lg bg-gray-100 hover:bg-gray-200 disabled:opacity-40 disabled:cursor-not-allowed transition-colors text-sm text-gray-600"
                >
                  Next <ChevronRight size={15} />
                </button>
              </div>
            )}
          </>

        ) : simplified ? (
          /* Simplified flat list -- no accordion; paginated for performance */
          <>
            <div ref={simplifiedTopRef} />
            <table className="w-full">
              <CatalogTableHead />
              <tbody>
                {(() => {
                  const data = categoryCache[activeChip];
                  if (!data) return (
                    <tr><td colSpan={4} className="py-12 text-center text-gray-400 text-sm">Loading…</td></tr>
                  );
                  if (data.length === 0) return (
                    <tr><td colSpan={4} className="py-8 text-center text-gray-400 text-sm">No plants found.</td></tr>
                  );
                  const pageRows = data.slice(simplifiedPage * PAGE_SIZE, (simplifiedPage + 1) * PAGE_SIZE);
                  return pageRows.map(record => (
                    <VarietyRow
                      key={record.id}
                      record={record}
                      isAdded={isAdded(record)}
                      isAtLimit={isAtLimit && !isAdded(record)}
                      onAdd={onAdd}
                      onUpgrade={onUpgrade}
                    />
                  ));
                })()}
              </tbody>
            </table>
            {(() => {
              const data = categoryCache[activeChip];
              if (!data || data.length <= PAGE_SIZE) return null;
              const simplifiedTotalPages = Math.ceil(data.length / PAGE_SIZE);
              return (
                <div className="flex items-center justify-between px-4 py-3 border-t border-gray-100">
                  <button
                    onClick={() => setSimplifiedPage(p => p - 1)}
                    disabled={simplifiedPage === 0}
                    className="flex items-center gap-1 px-3 py-1.5 rounded-lg bg-gray-100 hover:bg-gray-200 disabled:opacity-40 disabled:cursor-not-allowed transition-colors text-sm text-gray-600"
                  >
                    <ChevronLeft size={15} /> Previous
                  </button>
                  <span className="text-sm text-gray-400">
                    Page {simplifiedPage + 1} of {simplifiedTotalPages} &middot; {data.length} plants
                  </span>
                  <button
                    onClick={() => setSimplifiedPage(p => p + 1)}
                    disabled={simplifiedPage >= simplifiedTotalPages - 1}
                    className="flex items-center gap-1 px-3 py-1.5 rounded-lg bg-gray-100 hover:bg-gray-200 disabled:opacity-40 disabled:cursor-not-allowed transition-colors text-sm text-gray-600"
                  >
                    Next <ChevronRight size={15} />
                  </button>
                </div>
              );
            })()}
          </>

        ) : activeChip === 'all' ? (
          <>
            <table className="w-full">
              <CatalogTableHead />
              <tbody>
                {allRecords.length === 0 ? (
                  <tr>
                    <td colSpan={4} className="py-12 text-center text-gray-400 text-sm">No records.</td>
                  </tr>
                ) : (
                  allRecords.map(record => (
                    <VarietyRow
                      key={record.id}
                      record={record}
                      isAdded={isAdded(record)}
                      isAtLimit={isAtLimit && !isAdded(record)}
                      onAdd={onAdd}
                      onUpgrade={onUpgrade}
                    />
                  ))
                )}
              </tbody>
            </table>
            {allTotalPages > 1 && (
              <div className="flex items-center justify-between px-4 py-3 border-t border-gray-100">
                <button
                  onClick={() => setAllPage(p => p - 1)}
                  disabled={allPage === 0}
                  className="flex items-center gap-1 px-3 py-1.5 rounded-lg bg-gray-100 hover:bg-gray-200 disabled:opacity-40 disabled:cursor-not-allowed transition-colors text-sm text-gray-600"
                >
                  <ChevronLeft size={15} /> Previous
                </button>
                <span className="text-sm text-gray-400">
                  Page {allPage + 1} of {allTotalPages} &middot; {allTotal} plants
                </span>
                <button
                  onClick={() => setAllPage(p => p + 1)}
                  disabled={allPage >= allTotalPages - 1}
                  className="flex items-center gap-1 px-3 py-1.5 rounded-lg bg-gray-100 hover:bg-gray-200 disabled:opacity-40 disabled:cursor-not-allowed transition-colors text-sm text-gray-600"
                >
                  Next <ChevronRight size={15} />
                </button>
              </div>
            )}
          </>

        ) : activeChip === 'vegetables' ? (
          vegetableGroups
            ? PLANT_GROUP_ORDER
              .filter(groupKey => vegetableGroups[groupKey])
              .map(groupKey => (
                <PlantGroupSection
                  key={groupKey}
                  groupKey={groupKey}
                  plantMap={vegetableGroups[groupKey]}
                  isExpanded={expandedGroups.has(groupKey)}
                  onToggleGroup={() => toggleGroup(groupKey)}
                  expandedPlant={expandedVegPlant[groupKey] || null}
                  onTogglePlant={toggleVegPlant}
                  isAdded={isAdded}
                  isAtLimit={isAtLimit}
                  onAdd={onAdd}
                  onUpgrade={onUpgrade}
                />
              ))
            : <div className="py-12 text-center text-gray-400 text-sm">Loading…</div>

        ) : activeChip === 'flowers' ? (
          flowerGroups
            ? FLOWER_GROUP_ORDER
              .filter(groupKey => flowerGroups[groupKey])
              .map(groupKey => (
                <PlantGroupSection
                  key={groupKey}
                  groupKey={groupKey}
                  plantMap={flowerGroups[groupKey]}
                  isExpanded={expandedFlowerGroups.has(groupKey)}
                  onToggleGroup={() => toggleFlowerGroup(groupKey)}
                  expandedPlant={expandedFlowerPlant[groupKey] || null}
                  onTogglePlant={toggleFlowerPlant}
                  isAdded={isAdded}
                  isAtLimit={isAtLimit}
                  onAdd={onAdd}
                  onUpgrade={onUpgrade}
                />
              ))
            : <div className="py-12 text-center text-gray-400 text-sm">Loading…</div>

        ) : (
          singleCategoryPlantMap
            ? Object.keys(singleCategoryPlantMap).sort().map(plantName => (
              <PlantRow
                key={plantName}
                plantName={plantName}
                varieties={singleCategoryPlantMap[plantName]}
                isExpanded={expandedPlant === plantName}
                onToggle={() => toggleSinglePlant(plantName)}
                isAdded={isAdded}
                isAtLimit={isAtLimit}
                onAdd={onAdd}
                onUpgrade={onUpgrade}
                indent={false}
              />
            ))
            : <div className="py-12 text-center text-gray-400 text-sm">Loading…</div>
        )}
      </div>

      {/* Upgrade nudge */}
      {isAtLimit && (
        <div className="mb-4 p-3 bg-amber-50 border border-amber-200 rounded-xl text-sm text-amber-800 text-center">
          You&apos;ve reached the {seedLimit}-plant free limit.{' '}
          <button
            onClick={onUpgrade}
            className="font-semibold underline hover:text-amber-900 transition-colors"
          >
            Upgrade to Pro
          </button>{' '}for unlimited plants.
        </div>
      )}

      {/* Custom plant CTA — hidden in simplified mode (shown in host modal's sticky footer) */}
      {!simplified && (
        <div className="text-center py-3 border-t border-gray-100">
          <button
            onClick={onSwitchToAdd}
            className="text-[#52A675] hover:text-[#4A9168] text-sm font-medium"
          >
            Can&apos;t find your variety? Add it custom &rarr;
          </button>
        </div>
      )}
    </div>
  );
};

export default CatalogBrowser;
